import { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { brand } from "../../../config/theme";
import type { buildThemeClasses } from "../theme";
import {
  chatConfigErrorMessage,
  fetchChatConfig,
  saveChatConfig,
  useChatPermissions,
  DEFAULT_MENU_PRODUCT,
  MENU_PRODUCTS,
  WELCOME_MENU_LIMITS,
  WELCOME_MENU_PRESETS,
  WELCOME_MENU_TOOLS,
  channelAccountLabel,
  DEFAULT_HANDOFF_REPLY,
  type ChannelAccount,
  type MenuProduct,
  type WelcomeMenu,
  type WelcomeMenuAction,
  type WelcomeMenuOption,
} from "../services/chatService";
import { fetchShortLinks, type ShortLink } from "../services/shortLinksService";
import { trackWelcomeMenuSaved } from "../../../analytics/crmAnalytics";

// ===========================================================================
// Configuración → Chat → «Menú de bienvenida» (#1525).
//
// El menú que la bandeja manda sola al PRIMER mensaje de cada conversación:
// una lista interactiva en WhatsApp (botón que despliega las opciones) o el
// mismo menú numerado en Messenger/Instagram. Cada opción es un enlace (manda
// la URL con los UTM del chatbot) o una acción del agente de IA (agendar demo).
//
// Hay un menú POR PRODUCTO (#1536): Wayak Logistics (B2B) y Movik (B2C)
// comparten la bandeja, pero cada número de WhatsApp (o página) habla de su
// producto. El selector «Producto del menú» elige qué menú se edita y la tabla
// «Qué menú usa cada número» asigna cada número a un producto (sin asignar = Wayak).
//
// La pantalla edita los menús COMPLETOS y los guarda de una vez (PUT, no parche).
// Los límites son los de la lista de WhatsApp; la API los vuelve a validar y
// responde 422 con el campo que falló. Guardar es sólo de administradores del
// chat: a los demás se les muestra el menú en sólo lectura.
// ===========================================================================

type TC = ReturnType<typeof buildThemeClasses>;

/** «Prueba piloto» → «prueba-piloto» (id de fila de WhatsApp: minúsculas, letras, números, - y _). */
export function slugifyOptionId(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
}

/** Qué le falta a una opción para poder guardarse (vacío = nada). */
export function optionProblems(option: WelcomeMenuOption): string[] {
  const out: string[] = [];
  if (!/^[a-z0-9][a-z0-9_-]{0,39}$/.test(option.id)) out.push("id");
  if (!option.title.trim() || option.title.length > WELCOME_MENU_LIMITS.optionTitle) out.push("título");
  if (option.description.length > WELCOME_MENU_LIMITS.optionDescription) out.push("descripción");
  // Sólo https; el dominio lo valida la API (lista de hosts propios) y responde 422 `option_url_host`.
  if (option.action === "link" && !/^https:\/\/\S+$/i.test(option.url ?? "")) out.push("URL");
  if (option.action === "tool" && !WELCOME_MENU_TOOLS.some((t) => t.id === option.tool)) out.push("acción");
  if ((option.replyText ?? "").length > WELCOME_MENU_LIMITS.replyText) out.push("texto de traspaso");
  return out;
}

function newOption(): WelcomeMenuOption {
  return { id: "", title: "", description: "", action: "link", url: "" };
}

/** Una fila de «qué menú usa cada número». */
interface AccountRow {
  id: string;
  product: MenuProduct;
}

/**
 * Lo que le falta a la tabla de números para guardarse (vacío = nada). Sólo se
 * aceptan números conectados a la bandeja (`known`); la API también lo valida.
 */
export function accountProblems(rows: AccountRow[], known?: ReadonlySet<string>): string[] {
  const out: string[] = [];
  const ids = rows.map((r) => r.id.trim());
  if (ids.some((id) => !id)) out.push("elige un número");
  if (known && ids.some((id) => id && !known.has(id))) out.push("número no conectado");
  if (new Set(ids).size !== ids.length) out.push("número repetido");
  return out;
}

const HANDOFF_TOOL = "escalar_a_humano";

const productLabel = (product: MenuProduct) => MENU_PRODUCTS.find((p) => p.value === product)?.label ?? product;

const cleanMenu = (menu: WelcomeMenu): WelcomeMenu => ({
  ...menu,
  options: menu.options.map((o) => {
    const base = { id: o.id.trim(), title: o.title.trim(), description: o.description.trim(), action: o.action };
    if (o.action === "link") {
      return { ...base, url: (o.url ?? "").trim(), ...(o.shortCode?.trim() ? { shortCode: o.shortCode.trim() } : {}) };
    }
    const tool = o.tool ?? WELCOME_MENU_TOOLS[0].id;
    return { ...base, tool, ...(tool === HANDOFF_TOOL && o.replyText?.trim() ? { replyText: o.replyText.trim() } : {}) };
  }),
});

// `isDarkMode` se acepta por compatibilidad con CC-CONSOLE; en WAYAK el tema sale de los tokens.
export default function WelcomeMenuPanel({ tc }: { tc: TC; isDarkMode?: boolean }) {
  const permissions = useChatPermissions();
  const canSave = permissions.loaded && permissions.isAdmin;

  const [menus, setMenus] = useState<Record<MenuProduct, WelcomeMenu> | null>(null);
  const [product, setProduct] = useState<MenuProduct>(DEFAULT_MENU_PRODUCT);
  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [channelAccounts, setChannelAccounts] = useState<ChannelAccount[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const menu = menus ? menus[product] : null;
  /** Cambia el menú del producto elegido. */
  const setMenu = (update: (m: WelcomeMenu) => WelcomeMenu) =>
    setMenus((ms) => (ms ? { ...ms, [product]: update(ms[product]) } : ms));
  const applyConfig = (cfg: {
    welcomeMenus: Record<MenuProduct, WelcomeMenu>;
    accountProducts: Record<string, MenuProduct>;
    channelAccounts: ChannelAccount[];
  }) => {
    setMenus(cfg.welcomeMenus);
    setChannelAccounts(cfg.channelAccounts);
    setAccounts(Object.entries(cfg.accountProducts).map(([id, p]) => ({ id, product: p })));
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchChatConfig(controller.signal)
      .then(applyConfig)
      .catch((err: unknown) => {
        if ((err as { name?: string })?.name === "CanceledError" || (err as { name?: string })?.name === "AbortError") return;
        setLoadError(chatConfigErrorMessage(err, "No se pudo leer la configuración del chat."));
      });
    return () => controller.abort();
  }, []);

  // Enlaces cortos activos (CRM → Marketing → Enlaces, #1522) para elegir el de
  // cada opción sin teclear el código. Si la lista no llega (sin permiso, sin
  // enlaces o error), el campo se queda como texto libre.
  const [shortLinks, setShortLinks] = useState<ShortLink[]>([]);
  useEffect(() => {
    const controller = new AbortController();
    fetchShortLinks(controller.signal)
      .then((links) => setShortLinks(links.filter((l) => l.active)))
      .catch(() => setShortLinks([]));
    return () => controller.abort();
  }, []);
  const selectShortLink = (index: number, code: string) => {
    const link = shortLinks.find((l) => l.code === code);
    // Al elegir un enlace, su destino pasa a ser la URL de la opción (editable después).
    patchOption(index, link ? { shortCode: link.code, url: link.target } : { shortCode: code || undefined });
  };

  const inputClass = `w-full rounded-lg border px-3 py-2 text-sm outline-none border-line bg-app text-content disabled:opacity-60`;
  const labelClass = `mb-1 block text-xs font-medium ${tc.textSecondary}`;

  const patch = (p: Partial<WelcomeMenu>) => {
    setMenu((m) => ({ ...m, ...p }));
    setDirty(true);
  };
  const patchOption = (index: number, p: Partial<WelcomeMenuOption>) => {
    setMenu((m) => ({ ...m, options: m.options.map((o, i) => (i === index ? { ...o, ...p } : o)) }));
    setDirty(true);
  };
  const patchAccount = (index: number, p: Partial<AccountRow>) => {
    setAccounts((rows) => rows.map((r, i) => (i === index ? { ...r, ...p } : r)));
    setDirty(true);
  };
  const addAccount = () => {
    // El primer número conectado que todavía no tiene menú asignado.
    setAccounts((rows) => {
      const free = channelAccounts.find((a) => !rows.some((r) => r.id === a.id));
      return [...rows, { id: free?.id ?? "", product }];
    });
    setDirty(true);
  };
  const removeAccount = (index: number) => {
    setAccounts((rows) => rows.filter((_, i) => i !== index));
    setDirty(true);
  };
  const removeOption = (index: number) => patch({ options: (menu?.options ?? []).filter((_, i) => i !== index) });
  const addOption = () => {
    if (!menu || menu.options.length >= WELCOME_MENU_LIMITS.options) return;
    patch({ options: [...menu.options, newOption()] });
  };
  const changeAction = (index: number, action: WelcomeMenuAction) => {
    const current = menu?.options[index];
    if (!current) return;
    patchOption(index, action === "link"
      ? { action, url: current.url ?? "", tool: undefined }
      : { action, tool: current.tool ?? WELCOME_MENU_TOOLS[0].id, url: undefined, shortCode: undefined });
  };

  const preset = WELCOME_MENU_PRESETS[product];
  const loadPreset = async () => {
    if (!menu) return;
    if (menu.options.length > 0) {
      const res = await Swal.fire({
        icon: "question",
        title: `Cargar preset ${product}`,
        text: `Se reemplazarán las opciones actuales por las de ${productLabel(product)}: ${preset.options.map((o) => o.title).join(", ")}.`,
        showCancelButton: true,
        confirmButtonText: "Cargar",
        cancelButtonText: "Cancelar",
        confirmButtonColor: brand.primary,
      });
      if (!res.isConfirmed) return;
    }
    patch({
      title: preset.title,
      body: preset.body,
      buttonText: preset.buttonText,
      options: preset.options.map((o) => ({ ...o })),
    });
  };

  const problems = (menu?.options ?? []).map(optionProblems);
  const knownAccounts = new Set(channelAccounts.map((a) => a.id));
  const accountIssues = accountProblems(accounts, knownAccounts);
  /** El primer producto con un menú que no se puede guardar, y por qué. */
  const firstInvalid = (): { product: MenuProduct; reason: string } | null => {
    for (const { value } of MENU_PRODUCTS) {
      const m = menus?.[value];
      if (!m) continue;
      if (m.enabled && m.options.length === 0) return { product: value, reason: "Para activar el menú hace falta al menos una opción." };
      if (m.options.some((o) => optionProblems(o).length > 0)) return { product: value, reason: "Hay opciones incompletas o fuera de los límites de WhatsApp." };
    }
    return null;
  };

  const save = async () => {
    if (!menus || !canSave) return;
    const invalid = firstInvalid();
    if (invalid || accountIssues.length) {
      if (invalid) setProduct(invalid.product);
      await Swal.fire({
        icon: "warning",
        title: invalid ? `Revisa el menú de ${productLabel(invalid.product)}` : "Revisa los números",
        text: invalid ? invalid.reason : `Revisa en la tabla de números: ${accountIssues.join(", ")}.`,
        confirmButtonColor: brand.primary,
      });
      return;
    }
    setSaving(true);
    try {
      const welcomeMenus = { Wayak: cleanMenu(menus.Wayak), Movik: cleanMenu(menus.Movik) };
      const accountProducts = Object.fromEntries(accounts.map((r) => [r.id.trim(), r.product]));
      // `welcomeMenu` (= el de Wayak) se sigue mandando para la API de un solo menú.
      const saved = await saveChatConfig({ welcomeMenus, welcomeMenu: welcomeMenus.Wayak, accountProducts });
      applyConfig(saved);
      setDirty(false);
      const current = saved.welcomeMenus[product];
      trackWelcomeMenuSaved({ enabled: Boolean(current.enabled), optionCount: current.options?.length ?? 0, product });
      await Swal.fire({
        icon: "success",
        title: "Menú guardado",
        text: current.enabled
          ? `El menú de ${productLabel(product)} se mandará al primer mensaje de cada conversación nueva de sus números.`
          : `El menú de ${productLabel(product)} queda apagado.`,
        confirmButtonColor: brand.primary,
      });
    } catch (err) {
      await Swal.fire({ icon: "error", title: "No se pudo guardar", text: chatConfigErrorMessage(err), confirmButtonColor: brand.primary });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className={`mt-8 rounded-2xl border p-5 ${tc.cardBorder} ${tc.cardBg}`} aria-labelledby="welcome-menu-title">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-500/15 text-emerald-500">
            <i className="ri-list-check-2 text-lg" />
          </span>
          <div>
            <h2 id="welcome-menu-title" className={`text-base font-bold ${tc.text}`}>Menú de bienvenida</h2>
            <p className={`text-xs ${tc.textSecondary}`}>
              Se manda solo al primer mensaje de cada conversación: lista interactiva en WhatsApp, numerado en Messenger e Instagram.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={loadPreset}
            disabled={!menu || !canSave}
            className={`rounded-lg border px-3 py-2 text-sm ${tc.border} ${tc.textSecondary} ${tc.buttonHover} disabled:opacity-50`}
          >
            <i className="ri-magic-line mr-1" />
            Cargar preset {product}
          </button>
          <button
            type="button"
            onClick={save}
            disabled={!menu || !canSave || saving}
            className="rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white hover:bg-primary-400 disabled:opacity-50"
          >
            {saving ? "Guardando…" : "Guardar menú"}
          </button>
        </div>
      </div>

      {permissions.loaded && !permissions.isAdmin && (
        <p className="mb-3 rounded-lg bg-amber-500/10 px-3 py-2 text-xs text-amber-600 dark:text-amber-400">
          Sólo los administradores del chat pueden guardar este menú. Lo ves en sólo lectura.
        </p>
      )}

      {loadError && <p className="mb-3 text-sm text-red-500">{loadError}</p>}
      {!menu && !loadError && <p className={`text-sm ${tc.textSecondary}`}>Cargando configuración…</p>}

      {menu && (
        <div className="space-y-4">
          <div className="max-w-xs">
            <label className={labelClass} htmlFor="wm-product">Producto del menú</label>
            <select id="wm-product" className={inputClass} value={product} onChange={(e) => setProduct(e.target.value as MenuProduct)}>
              {MENU_PRODUCTS.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>

          <label className={`flex items-center gap-2 text-sm ${tc.text}`}>
            <input
              type="checkbox"
              checked={menu.enabled}
              disabled={!canSave}
              onChange={(e) => patch({ enabled: e.target.checked })}
              className="h-4 w-4 accent-primary-500"
            />
            Activar el menú de bienvenida
          </label>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <div>
              <label className={labelClass} htmlFor="wm-title">Encabezado <span className="font-normal">({menu.title.length}/{WELCOME_MENU_LIMITS.title})</span></label>
              <input id="wm-title" className={inputClass} maxLength={WELCOME_MENU_LIMITS.title} value={menu.title} disabled={!canSave} onChange={(e) => patch({ title: e.target.value })} />
            </div>
            <div>
              <label className={labelClass} htmlFor="wm-button">Texto del botón <span className="font-normal">({menu.buttonText.length}/{WELCOME_MENU_LIMITS.buttonText})</span></label>
              <input id="wm-button" className={inputClass} maxLength={WELCOME_MENU_LIMITS.buttonText} value={menu.buttonText} disabled={!canSave} onChange={(e) => patch({ buttonText: e.target.value })} />
            </div>
            <div className="md:col-span-3">
              <label className={labelClass} htmlFor="wm-body">Cuerpo</label>
              <textarea id="wm-body" rows={2} className={inputClass} maxLength={WELCOME_MENU_LIMITS.body} value={menu.body} disabled={!canSave} onChange={(e) => patch({ body: e.target.value })} />
            </div>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <p className={`text-xs font-medium ${tc.textSecondary}`}>
                Opciones ({menu.options.length}/{WELCOME_MENU_LIMITS.options}) · título ≤{WELCOME_MENU_LIMITS.optionTitle}, descripción ≤{WELCOME_MENU_LIMITS.optionDescription}
              </p>
              <button
                type="button"
                onClick={addOption}
                disabled={!canSave || menu.options.length >= WELCOME_MENU_LIMITS.options}
                className={`rounded-lg border px-3 py-1.5 text-xs ${tc.border} ${tc.textSecondary} ${tc.buttonHover} disabled:opacity-50`}
              >
                <i className="ri-add-line mr-1" />
                Agregar opción
              </button>
            </div>

            {menu.options.length === 0 ? (
              <p className={`rounded-lg border border-dashed p-4 text-center text-xs ${tc.cardBorder} ${tc.textSecondary}`}>
                Sin opciones. Agrega una o carga el preset de {product}.
              </p>
            ) : (
              <ul className="space-y-2">
                {menu.options.map((o, i) => (
                  <li key={i} className={`rounded-xl border p-3 ${tc.cardBorder}`} data-testid={`welcome-option-${i}`}>
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-12">
                      <div className="md:col-span-3">
                        <label className={labelClass} htmlFor={`wm-opt-title-${i}`}>Título</label>
                        <input
                          id={`wm-opt-title-${i}`}
                          className={inputClass}
                          maxLength={WELCOME_MENU_LIMITS.optionTitle}
                          value={o.title}
                          disabled={!canSave}
                          onChange={(e) => patchOption(i, { title: e.target.value, ...(o.id ? {} : { id: slugifyOptionId(e.target.value) }) })}
                          placeholder="Prueba piloto"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className={labelClass} htmlFor={`wm-opt-id-${i}`}>Id</label>
                        <input id={`wm-opt-id-${i}`} className={inputClass} value={o.id} disabled={!canSave} onChange={(e) => patchOption(i, { id: slugifyOptionId(e.target.value) })} placeholder="piloto" />
                      </div>
                      <div className="md:col-span-5">
                        <label className={labelClass} htmlFor={`wm-opt-desc-${i}`}>Descripción</label>
                        <input id={`wm-opt-desc-${i}`} className={inputClass} maxLength={WELCOME_MENU_LIMITS.optionDescription} value={o.description} disabled={!canSave} onChange={(e) => patchOption(i, { description: e.target.value })} />
                      </div>
                      <div className="md:col-span-2">
                        <label className={labelClass} htmlFor={`wm-opt-action-${i}`}>Tipo</label>
                        <select id={`wm-opt-action-${i}`} className={inputClass} value={o.action} disabled={!canSave} onChange={(e) => changeAction(i, e.target.value as WelcomeMenuAction)}>
                          <option value="link">Enlace</option>
                          <option value="tool">Acción de la IA</option>
                        </select>
                      </div>
                      {o.action === "link" ? (
                        <>
                          <div className="md:col-span-8">
                            <label className={labelClass} htmlFor={`wm-opt-url-${i}`}>URL (https, dominios permitidos de WAYAK / Movik)</label>
                            <input id={`wm-opt-url-${i}`} className={inputClass} value={o.url ?? ""} disabled={!canSave} onChange={(e) => patchOption(i, { url: e.target.value })} placeholder="https://wayak-logistics.com/#/prueba-piloto" />
                          </div>
                          <div className="md:col-span-3">
                            <label className={labelClass} htmlFor={`wm-opt-code-${i}`}>Código de enlace corto</label>
                            {shortLinks.length > 0 ? (
                              <select id={`wm-opt-code-${i}`} className={inputClass} value={o.shortCode ?? ""} disabled={!canSave} onChange={(e) => selectShortLink(i, e.target.value)}>
                                <option value="">Sin enlace corto</option>
                                {/* Un código guardado que ya no está activo se sigue mostrando para no perderlo al guardar. */}
                                {o.shortCode && !shortLinks.some((l) => l.code === o.shortCode) ? <option value={o.shortCode}>{o.shortCode}</option> : null}
                                {shortLinks.map((l) => (
                                  <option key={l.code} value={l.code}>{l.label ? `${l.label} (${l.code})` : l.code}</option>
                                ))}
                              </select>
                            ) : (
                              <input id={`wm-opt-code-${i}`} className={inputClass} value={o.shortCode ?? ""} disabled={!canSave} onChange={(e) => patchOption(i, { shortCode: e.target.value })} placeholder="opcional, va como ref=" />
                            )}
                          </div>
                        </>
                      ) : (
                        <div className="md:col-span-11 space-y-2">
                          <div>
                            <label className={labelClass} htmlFor={`wm-opt-tool-${i}`}>Acción</label>
                            <select id={`wm-opt-tool-${i}`} className={inputClass} value={o.tool ?? WELCOME_MENU_TOOLS[0].id} disabled={!canSave} onChange={(e) => patchOption(i, { tool: e.target.value })}>
                              {WELCOME_MENU_TOOLS.map((t) => (
                                <option key={t.id} value={t.id}>{t.label}</option>
                              ))}
                            </select>
                          </div>
                          {o.tool === HANDOFF_TOOL && (
                            <div>
                              <label className={labelClass} htmlFor={`wm-opt-reply-${i}`}>
                                Texto al pasar con una persona <span className="font-normal">(se manda sin IA; vacío = el de omisión)</span>
                              </label>
                              <textarea
                                id={`wm-opt-reply-${i}`}
                                rows={2}
                                className={inputClass}
                                maxLength={WELCOME_MENU_LIMITS.replyText}
                                value={o.replyText ?? ""}
                                disabled={!canSave}
                                placeholder={DEFAULT_HANDOFF_REPLY}
                                onChange={(e) => patchOption(i, { replyText: e.target.value })}
                              />
                            </div>
                          )}
                        </div>
                      )}
                      <div className="flex items-end justify-end md:col-span-1">
                        <button
                          type="button"
                          title="Quitar opción"
                          aria-label={`Quitar opción ${o.title || i + 1}`}
                          onClick={() => removeOption(i)}
                          disabled={!canSave}
                          className="rounded-md p-1.5 text-red-500 hover:bg-red-500/10 disabled:opacity-50"
                        >
                          <i className="ri-delete-bin-line" />
                        </button>
                      </div>
                    </div>
                    {problems[i].length > 0 && (
                      <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">Revisa: {problems[i].join(", ")}.</p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className={`rounded-xl border p-3 ${tc.cardBorder}`} data-testid="welcome-accounts">
            <div className="mb-2 flex items-center justify-between gap-2">
              <p className={`text-xs font-medium ${tc.textSecondary}`}>
                Qué menú usa cada número · números de WhatsApp, páginas y cuentas de Instagram conectados a la bandeja. Sin asignar = Wayak.
              </p>
              <button
                type="button"
                onClick={addAccount}
                disabled={!canSave || accounts.length >= channelAccounts.length}
                className={`shrink-0 rounded-lg border px-3 py-1.5 text-xs ${tc.border} ${tc.textSecondary} ${tc.buttonHover} disabled:opacity-50`}
              >
                <i className="ri-add-line mr-1" />
                Asignar número
              </button>
            </div>
            {channelAccounts.length === 0 && accounts.length === 0 ? (
              <p className={`text-xs ${tc.textSecondary}`}>No hay números conectados a la bandeja: todos usan el menú de Wayak.</p>
            ) : accounts.length === 0 ? (
              <p className={`text-xs ${tc.textSecondary}`}>Todos los números usan el menú de Wayak.</p>
            ) : (
              <ul className="space-y-2">
                {accounts.map((r, i) => (
                  <li key={i} className="grid grid-cols-1 gap-2 md:grid-cols-12" data-testid={`welcome-account-${i}`}>
                    <div className="md:col-span-6">
                      <label className={labelClass} htmlFor={`wm-acc-id-${i}`}>Número / página</label>
                      <select id={`wm-acc-id-${i}`} className={inputClass} value={r.id} disabled={!canSave} onChange={(e) => patchAccount(i, { id: e.target.value })}>
                        <option value="">Elige un número…</option>
                        {/* Una asignación guardada cuyo número ya no está conectado se muestra para poder quitarla. */}
                        {r.id && !knownAccounts.has(r.id) && <option value={r.id}>{r.id} (no conectado)</option>}
                        {channelAccounts
                          .filter((a) => a.id === r.id || !accounts.some((other) => other.id === a.id))
                          .map((a) => (
                            <option key={a.id} value={a.id}>{channelAccountLabel(a)}</option>
                          ))}
                      </select>
                    </div>
                    <div className="md:col-span-5">
                      <label className={labelClass} htmlFor={`wm-acc-product-${i}`}>Menú</label>
                      <select id={`wm-acc-product-${i}`} className={inputClass} value={r.product} disabled={!canSave} onChange={(e) => patchAccount(i, { product: e.target.value as MenuProduct })}>
                        {MENU_PRODUCTS.map((p) => (
                          <option key={p.value} value={p.value}>{p.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-end justify-end md:col-span-1">
                      <button
                        type="button"
                        aria-label={`Quitar número ${r.id || i + 1}`}
                        onClick={() => removeAccount(i)}
                        disabled={!canSave}
                        className="rounded-md p-1.5 text-red-500 hover:bg-red-500/10 disabled:opacity-50"
                      >
                        <i className="ri-delete-bin-line" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {accountIssues.length > 0 && <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">Revisa: {accountIssues.join(", ")}.</p>}
          </div>

          <p className={`text-xs ${tc.textSecondary}`}>
            <i className="ri-information-line mr-1" />
            Los enlaces se mandan con seguimiento de campaña del chatbot; las acciones las resuelve la IA (Agendar demo, Pasar con una persona). El menú se manda una sola vez por conversación.
            {dirty && <span className="ml-1 font-medium">Hay cambios sin guardar.</span>}
          </p>
        </div>
      )}
    </section>
  );
}
